import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToClients, subscribeToAllObjects, isProfileComplete } from '../utils/storage';
import { getPermissions } from '../utils/permissions';
import AccessDeniedModal from './AccessDeniedModal';
import CreateClientModal from './CreateClientModal';
import { SearchBar, EmptyState } from './templates';
import './TestSites.css';

export default function TestSites() {
  const [clients, setClients] = useState([]);
  const [objectCounts, setObjectCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddDenied, setShowAddDenied] = useState(false);
  const [search, setSearch] = useState('');

  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const perms = getPermissions(userProfile?.roles || []);

  // Real-time listener for clients
  useEffect(() => {
    const unsubscribe = subscribeToClients((clientList) => {
      setClients(clientList);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Single real-time listener for ALL objects — counts update automatically
  useEffect(() => {
    const unsubscribe = subscribeToAllObjects((allObjects) => {
      const map = {};
      allObjects.forEach((obj) => {
        map[obj.clientId] = (map[obj.clientId] || 0) + 1;
      });
      setObjectCounts(map);
    });
    return () => unsubscribe();
  }, []);

  function handleAddTestSiteClick() {
    if (perms.canAddTestSite) {
      setShowCreateModal(true);
    } else {
      setShowAddDenied(true);
    }
  }

  function handleClientCreated() {
    setShowCreateModal(false);
  }

  const filteredClients = search.trim()
    ? clients.filter((c) =>
        c.clientName && c.clientName.toLowerCase().includes(search.trim().toLowerCase())
      )
    : clients;

  if (loading) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="toolbar">
        {clients.length > 0 && (
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search by client name..."
          />
        )}
        <button className="btn btn-primary" onClick={handleAddTestSiteClick}>
          + Add Test Site
        </button>
      </div>

      {clients.length === 0 ? (
        <EmptyState
          icon="📋"
          title="No test sites yet"
          description="Create your first test site to get started."
        >
          <button className="btn btn-primary" onClick={handleAddTestSiteClick}>
            + Add Test Site
          </button>
        </EmptyState>
      ) : (
        <>
          <div className="table-wrapper">
            <table className="ds-table site-table">
              <thead>
                <tr>
                  <th>Test Site ({filteredClients.length})</th>
                  <th>Objects</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="no-results">No test sites match your search.</td>
                  </tr>
                ) : (
                  [...filteredClients].sort((a, b) => a.name.localeCompare(b.name)).map((client) => {
                    const objectCount = objectCounts[client.id] || 0;
                    const complete = isProfileComplete(client);
                    return (
                      <tr
                        key={client.id}
                        className="site-row"
                        onClick={() => navigate(`/client/${client.id}`)}
                      >
                        <td className="site-name">
                          {client.name}
                          {!complete && <span className="row-alert">Incomplete</span>}
                        </td>
                        <td className="site-objects">{objectCount}</td>
                        <td className="site-arrow">&rsaquo;</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showCreateModal && (
        <CreateClientModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleClientCreated}
        />
      )}

      {showAddDenied && (
        <AccessDeniedModal
          roles={userProfile?.roles || []}
          onClose={() => setShowAddDenied(false)}
        />
      )}
    </div>
  );
}
